<!-- Modern Invoice List Dashboard -->
<div class="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">

  <!-- Header Section -->
  <div class="mb-8">
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 mb-2">
          {{ "Invoice Management" | translate }}
        </h1>
        <p class="text-gray-600">
          View and manage all invoices for your construction projects
        </p>
      </div>

      <!-- Add Invoice Button -->
      <button
        [routerLink]="'/home/add-invoice'"
        class="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-lg"
      >
        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
        </svg>
        Create Invoice
      </button>
    </div>
  </div>

  <!-- Stats Cards -->
  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
    <!-- Total Invoices -->
    <div class="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
      <div class="flex items-center gap-4">
        <div class="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
          <mat-icon class="text-blue-600">receipt_long</mat-icon>
        </div>
        <div>
          <p class="text-sm text-gray-500">Total Invoices</p>
          <p class="text-2xl font-bold text-gray-900">{{ dataSource.data.length }}</p>
        </div>
      </div>
    </div>

    <!-- Total Amount -->
    <div class="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
      <div class="flex items-center gap-4">
        <div class="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
          <mat-icon class="text-emerald-600">currency_rupee</mat-icon>
        </div>
        <div>
          <p class="text-sm text-gray-500">Total Amount</p>
          <p class="text-2xl font-bold text-gray-900">{{ formatCurrency(totalAmount) }}</p>
        </div>
      </div>
    </div>

    <!-- Paid -->
    <div class="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
      <div class="flex items-center gap-4">
        <div class="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
          <mat-icon class="text-green-600">check_circle</mat-icon>
        </div>
        <div>
          <p class="text-sm text-gray-500">Paid</p>
          <p class="text-2xl font-bold text-green-600">{{ paidCount }}</p>
        </div>
      </div>
    </div>

    <!-- Pending -->
    <div class="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
      <div class="flex items-center gap-4">
        <div class="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
          <mat-icon class="text-yellow-600">schedule</mat-icon>
        </div>
        <div>
          <p class="text-sm text-gray-500">Pending</p>
          <p class="text-2xl font-bold text-yellow-600">{{ pendingCount }}</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Search and Filter Section -->
  <div class="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-8">
    <div class="flex flex-col lg:flex-row gap-4">
      <!-- Search Input -->
      <div class="flex-1">
        <div class="relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search by invoice number, project, or vendor..."
            class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl leading-5 bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200"
            (input)="applyFilter($event)"
          />
        </div>
      </div>

      <!-- Status Filter -->
      <div class="lg:w-56">
        <select
          class="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 text-gray-700"
          (change)="filterByStatus($event)"
        >
          <option value="">All Statuses</option>
          <option value="submitted">Submitted</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <!-- Refresh Button -->
      <button
        (click)="fetchData()"
        class="inline-flex items-center px-4 py-3 border border-gray-300 rounded-xl text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
        [disabled]="isLoading"
      >
        <mat-icon [class.animate-spin]="isLoading">refresh</mat-icon>
      </button>
    </div>
  </div>

  <!-- Invoice Table Card -->
  <div class="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
    <!-- Table Header -->
    <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
      <h2 class="text-lg font-semibold text-gray-900">Invoice List</h2>
      <p class="text-sm text-gray-500 mt-1">All invoices for your projects</p>
    </div>

    <!-- Loading State -->
    <div *ngIf="isLoading" class="flex items-center justify-center py-12">
      <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      <span class="ml-3 text-gray-600">Loading invoices...</span>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto" *ngIf="!isLoading">
      <table mat-table [dataSource]="dataSource" matSort class="w-full">
        <!-- Serial Number Column -->
        <ng-container matColumnDef="srNo">
          <th mat-header-cell *matHeaderCellDef class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            #
          </th>
          <td mat-cell *matCellDef="let element; let i = index" class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
            {{ i + 1 }}
          </td>
        </ng-container>

        <!-- Invoice Number Column -->
        <ng-container matColumnDef="invoiceNo">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            Invoice No
          </th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap">
            <div class="flex items-center">
              <div class="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-3">
                <mat-icon class="text-indigo-600 !text-lg">receipt</mat-icon>
              </div>
              <span class="text-sm font-semibold text-gray-900">{{ element.invoice_no }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Project Column -->
        <ng-container matColumnDef="project">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            Project
          </th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap">
            <div>
              <div class="text-sm font-medium text-gray-900">
                {{ element.data?.projectName || ('Project #' + element.project_id) }}
              </div>
              <div class="text-xs text-gray-500">{{ element.data?.domain || 'N/A' }}</div>
            </div>
          </td>
        </ng-container>

        <!-- Vendor Column -->
        <ng-container matColumnDef="vendor">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            Vendor
          </th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap">
            <span class="text-sm text-gray-900">{{ element.data?.vendorName || 'N/A' }}</span>
          </td>
        </ng-container>

        <!-- Amount Column -->
        <ng-container matColumnDef="amount">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            Amount
          </th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap">
            <span class="text-sm font-semibold text-gray-900">{{ formatCurrency(element.total) }}</span>
          </td>
        </ng-container>

        <!-- Invoice Date Column -->
        <ng-container matColumnDef="invoiceDate">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            Date
          </th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap">
            <div class="flex items-center text-sm text-gray-700">
              <mat-icon class="!text-sm text-gray-400 mr-1">calendar_today</mat-icon>
              {{ (element.data?.invoiceDate || element.created_at) | date:'MMM dd, yyyy' }}
            </div>
          </td>
        </ng-container>

        <!-- Status Column -->
        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            Status
          </th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap">
            <span
              class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
              [ngClass]="getStatusClass(element)"
            >
              {{ element.data?.invoiceStatus || 'Submitted' }}
            </span>
          </td>
        </ng-container>

        <!-- Actions Column -->
        <ng-container matColumnDef="action">
          <th mat-header-cell *matHeaderCellDef class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            Actions
          </th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap">
            <div class="flex items-center space-x-1">
              <!-- Download Button -->
              <button
                (click)="downloadInvoice(element)"
                class="inline-flex items-center p-2 border border-transparent rounded-lg text-emerald-600 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors duration-200"
                title="Download Invoice PDF"
              >
                <mat-icon class="!text-lg">download</mat-icon>
              </button>

              <!-- Edit Button -->
              <button
                (click)="editInvoice(element)"
                class="inline-flex items-center p-2 border border-transparent rounded-lg text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                title="Edit Invoice"
              >
                <mat-icon class="!text-lg">edit</mat-icon>
              </button>

              <!-- Delete Button -->
              <button
                (click)="deleteInvoice(element)"
                class="inline-flex items-center p-2 border border-transparent rounded-lg text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors duration-200"
                title="Delete Invoice"
              >
                <mat-icon class="!text-lg">delete</mat-icon>
              </button>
            </div>
          </td>
        </ng-container>

        <!-- Table Header and Rows -->
        <tr mat-header-row *matHeaderRowDef="displayedColumns" class="bg-gray-50"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns" class="hover:bg-blue-50/50 transition-colors duration-150 border-b border-gray-100"></tr>
      </table>
    </div>

    <!-- Empty State -->
    <div *ngIf="!isLoading && dataSource.data.length === 0" class="text-center py-16">
      <div class="mx-auto w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <mat-icon class="text-gray-400 !text-4xl !w-10 !h-10">receipt_long</mat-icon>
      </div>
      <h3 class="text-lg font-medium text-gray-900 mb-2">No invoices found</h3>
      <p class="text-gray-500 mb-6 max-w-sm mx-auto">Get started by creating your first invoice for a project.</p>
      <button
        [routerLink]="'/home/add-invoice'"
        class="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
      >
        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
        </svg>
        Create First Invoice
      </button>
    </div>

    <!-- Pagination -->
    <div class="px-6 py-4 border-t border-gray-200 bg-gray-50" *ngIf="dataSource.data.length > 0">
      <mat-paginator
        [length]="dataSource.data.length"
        [pageSize]="10"
        [pageSizeOptions]="[5, 10, 25, 50]"
        showFirstLastButtons
        class="bg-transparent"
      >
      </mat-paginator>
    </div>
  </div>
</div>
